import React, { useMemo, useState, useEffect,useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { 
    ElementList, 
    VariableList, 
    Preview, 
    Configuration, 
    DocumentIndex,
    SuggestionList,
    AttachmentList,
    NotificationList,
    Share,
    Changelog,
    Questions,
    Comments,
    TagList
} from './Contents';
import { useEditorState } from '../editor-context'
import { useProfileAbility } from 'context/profile';

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
  
    return result;
  };
  

export default function useSidebarMenuOptions({ defaultTab, onDefaultTabChange } = {}) {
  const { t } = useTranslation()
  const [populatedDefaultTab, setPopulatedDefaultTab] = useState({})
  const { document: doc, documentType, mode, isEditable, isTemplate, isConfigOk, isParticipantsOk } = useEditorState()
  const profileAbility = useProfileAbility();
  const prevDefaultTab = usePrevious(defaultTab);
  const isMigrated = doc.isMigrated;

  const hasConfig = useMemo(() => {
        return doc.name && doc.type && doc.participants.length >= 1
          && doc.participants.every((participant) => !!participant.label);
      
  }, [doc]) 

    const alertContract = useMemo(() => {
        return !(isConfigOk && isParticipantsOk);
    }, [isConfigOk, isParticipantsOk]) 



  const tabsMap = useMemo(() => {
    return new Map([
        ['index', { 
            name: 'index', 
            title: t('editor.index.title'), 
            subtitle: t('editor.index.subtitle'), 
            img: '/images/editor/icons/index.svg',
            className: 'editor-sidebar-index',
            Component: DocumentIndex,
            shouldShow: () => {
                if (hasConfig && ['pdf', 'trato'].includes(documentType))  return true
                return false;
            }
        }],
        ['changerequest', { 
            name: 'changerequest', 
            title: t('editor.changerequest.title'), 
            subtitle: t('editor.changerequest.ask_changes'), 
            img: '/images/editor/icons/changerequest.svg',
            shouldShow: () => {
                // if (hasConfig && mode === 'template')  return true
                return false;
            }
        }],
        ['comments', { 
            name: 'comments', 
            title: t('editor.comments.title'), 
            subtitle: t('editor.comments.subtitle'), 
            img: '/images/editor/icons/comments.svg',
            className: 'editor-sidebar-comments',
            Component: Comments,
            shouldShow: () => {
                if (hasConfig && mode === 'contract')  return true
                return false;
            }
        }],
        ['variables', { 
            name: 'variables', 
            title: t('editor.variables.title'), 
            subtitle: t('editor.variables.subtitle'), 
            img: '/images/editor/icons/variables.svg',
            className: 'editor-sidebar-variables',
            Component: VariableList,
            shouldShow: () => {
                if (hasConfig && isEditable)  return true
                return false;
            }
        }],
        ['elements', { 
            name: 'elements', 
            title: t('editor.elements.title'), 
            subtitle: t('editor.elements.subtitle'), 
            img: '/images/editor/icons/elements.svg',
            Component: ElementList,
            shouldShow: () => {
                if (hasConfig 
                    && documentType === 'trato' 
                    && isEditable 
                    && isMigrated
                    && (doc.locked === false || mode === 'template')
                )  return true;

                return false;
            }
        }],
        ['suggestions', { 
            name: 'suggestions', 
            title: t('editor.suggestions.title'), 
            subtitle: t('editor.suggestions.subtitle'), 
            img: '/images/editor/icons/library.svg',
            className: 'editor-sidebar-suggestions',
            Component: SuggestionList,
            shouldShow: () => {
                if (hasConfig 
                    && documentType === 'trato' 
                    && isEditable 
                    && isMigrated
                    && (doc.locked === false || mode === 'template')
                )  return true;

                return false;
            }
        }],
        ['search-suggestions', { 
            name: 'search-suggestions', 
            title: t('editor.suggestions.analysis'), 
            subtitle: t('editor.suggestions.subtitle'), 
            img: '/images/editor/icons/smart.svg',
            shouldShow: () => {
                // if (hasConfig 
                //     && documentType === 'trato' 
                //     && isEditable 
                //     && doc.locked === false 
                //     && profileAbility.can('VIEW_METADATA_SEARCH') 
                //     && mode === 'contract'
                // )  return true;

                return false;
            }
        }],
        ['share',{ 
            name: 'share', 
            title: t('editor.share.title'), 
            subtitle: t('editor.share.subtitle'), 
            img: '/images/editor/icons/share.svg',
            className: 'editor-sidebar-share',
            Component: Share,
            shouldShow: () => {
                if (hasConfig && mode === 'template')  return true
                return false;
            }
        }],
        ['tags', { 
            name: 'tags', 
            title: t('editor.tags.title'), 
            subtitle: t('editor.tags.subtitle'), 
            img: '/images/editor/icons/tags.svg',
            Component: TagList,
            shouldShow: () => {
                if (hasConfig)  return true
                return false;
            }
        }],
        ['notifications', { 
            name: 'notifications', 
            title: t('editor.notifications.title'), 
            subtitle: t('editor.notifications.subtitle'), 
            img: '/images/editor/icons/notifications.svg',
            className: 'editor-sidebar-notification',
            Component: NotificationList,
            shouldShow: () => {
                if (hasConfig && isEditable)  return true
                return false;
            }
        }],
        ['attachments', { 
            name: 'attachments', 
            title: t('editor.attachments.title'), 
            subtitle: t('editor.attachments.subtitle'), 
            img: '/images/editor/icons/attachments.svg',
            className: 'editor-sidebar-attachments',
            Component: AttachmentList,
            shouldShow: () => {
                if (hasConfig)  return true
                return false;
            }
        }],
        ['questions', { 
            name: 'questions', 
            title: t('editor.question.title'), 
            subtitle: t('editor.question.subtitle'), 
            img: '/images/editor/icons/questionnaire.svg',
            className: 'editor-sidebar-questions',
            Component: Questions,
            shouldShow: () => {
                if (hasConfig 
                    && documentType === 'trato' 
                    && isEditable 
                    && mode === 'template'
                )  return true;

                return false;
            }
        }],
        ['preview', { 
            name: 'preview', 
            title: t('editor.preview.title'), 
            subtitle: t('editor.preview.subtitle'), 
            img: '/images/editor/icons/preview.svg',
            Component: Preview,
            className: 'editor-sidebar-preview',
            shouldShow: () => {
                if (hasConfig 
                    && documentType === 'trato' 
                    && isEditable 
                    && mode === 'contract'
                )  return true;
                return false;
            }
        }],
        ['changelog', { 
            name: 'changelog', 
            title: t('editor.changelog.title'), 
            subtitle: t('editor.changelog.subtitle'), 
            img: '/images/editor/icons/changelog.svg',
            className: 'editor-sidebar-changelog',
            Component: Changelog,
            shouldShow: () => {
                if (hasConfig && documentType === 'word')  return true
                return false;
            }
        }],
        ['configuration', { 
            name: 'configuration', 
            title: t('editor.config.title'), 
            subtitle: t('editor.config.subtitle'), 
            img: '/images/editor/icons/configuration.svg',
            Component: Configuration,
            className: 'editor-sidebar-config',
            showAlert: alertContract,
            shouldShow: () => {
                return true;
            }
        }],
    ])
  }, [t, hasConfig, documentType, alertContract, doc.locked, isEditable, mode, profileAbility, isMigrated])

  
  useEffect(() => {
    const pDefaultTab = tabsMap.get(defaultTab)
    setPopulatedDefaultTab(pDefaultTab ?? {})
    if (prevDefaultTab !== defaultTab) {
        onDefaultTabChange?.(pDefaultTab)
    }
  }, [defaultTab, prevDefaultTab, tabsMap])

  const tabs = useMemo(() => {
    const arr = Array.from(tabsMap.values())
    if (doc.elements?.length) {
        const previewPosition = 12;
        return reorder(arr, previewPosition, 0)
    }
    return arr;
  }, [tabsMap, doc.elements]);

  return { tabsMap, tabs, defaultTab: populatedDefaultTab };
}

function usePrevious(value) {
    // The ref object is a generic container whose current property is mutable ...
    // ... and can hold any value, similar to an instance property on a class
    const ref = useRef();
    
    // Store current value in ref
    useEffect(() => {
      ref.current = value;
    }, [value]); // Only re-run if value changes
    
    // Return previous value (happens before update in useEffect above)
    return ref.current;
  }